import React, { Suspense, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../../api';

import CommentTable from './CommentTable';
import CommentCards from './CommentCards';
import EditComment from './EditComment';
import Pagination from './Pagination';
import { Container } from "../layout/Container"
import { PageHeader } from "../layout/PageHeader"
import { toast } from "../shared/ToastProvider";
import EmptyState from "../shared/EmptyState";
import DeleteConfirmation from "../shared/DeleteConfirmation";
import LazyFallback from "../shared/LazyFallback";
import KpiCards from "../shared/KpiCards";

import { Card, CardHeader, CardTitle, CardContent } from "../ui/card";
import { Alert, AlertTitle, AlertDescription } from "../ui/alert";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Spinner } from "../ui/spinner";
import { Plus } from "lucide-react";

const CreateComment = React.lazy(() => import('./CreateComment'));

const Comments = () => {
    const [items, setItems] = useState([]);
    const [predictors, setPredictors] = useState([]);
    const [stocks, setStocks] = useState([]);
    const [newComment, setNewComment] = useState({
        comment_text: '',
        direction: '',
        time_frame: '',
        percentage_change: '',
        fk_predictor_id: '',
        fk_stock_symbol: '',
    });
    const [editComment, setEditComment] = useState(null);
    const [filterPredictorId, setFilterPredictorId] = useState(null);
    const [filterInputs, setFilterInputs] = useState({ reading_ease_min: '', reading_ease_max: '', grade_level_min: '', grade_level_max: '' });
    const [activeFilters, setActiveFilters] = useState({ reading_ease_min: '', reading_ease_max: '', grade_level_min: '', grade_level_max: '' });
    const [trigger, setTrigger] = useState(0);
    const [computeDate, setComputeDate] = useState('');
    const [computing, setComputing] = useState(false);
    const [computeMessage, setComputeMessage] = useState('');
    const location = useLocation();

    const [loading, setLoading] = useState(true);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 50;

    const [totalItems, setTotalItems] = useState(0);

    useEffect(() => {
        const fetchPredictorsAndStocks = async () => {
            try {
                const [predictorResponse, stockResponse] = await Promise.all([
                    api.get('/predictors'),
                    api.get('/stocks'),
                ]);
                setPredictors(predictorResponse.data);
                setStocks(stockResponse.data);
            } catch (error) {
                console.error('Erreur lors de la recuperation des predicteurs et stocks', error);
            }
        };
        fetchPredictorsAndStocks();
    }, []);

    useEffect(() => {
        const query = new URLSearchParams(location.search);
        const predictorId = query.get('predictor_id');
        const stockSymbol = query.get('stock_symbol');
        setFilterPredictorId(predictorId);

        const fetchData = async () => {
            setLoading(true);
            try {
                const params = {};
                if (predictorId) params.fk_predictor_id = predictorId;
                if (stockSymbol) params.fk_stock_symbol = stockSymbol;
                if (activeFilters.reading_ease_min) params.reading_ease_min = activeFilters.reading_ease_min;
                if (activeFilters.reading_ease_max) params.reading_ease_max = activeFilters.reading_ease_max;
                if (activeFilters.grade_level_min) params.grade_level_min = activeFilters.grade_level_min;
                if (activeFilters.grade_level_max) params.grade_level_max = activeFilters.grade_level_max;

                params.page = currentPage;
                params.limit = rowsPerPage;

                const response = await api.get('/comments', { params });
                const data = response?.data || [];
                setItems(Array.isArray(data) ? data : []);

                const total = response?.headers?.['x-total-count'];
                if (total != null) {
                    const count = parseInt(total, 10);
                    if (!isNaN(count)) {
                        setTotalItems(count);
                    }
                } else {
                    setTotalItems(0);
                }
            } catch (error) {
                console.error('Erreur lors de la recuperation des commentaires', error);
                setItems([]);
                setTotalItems(0);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, currentPage, trigger]);

    const resetFilter = () => {
        setFilterPredictorId(null);
        setCurrentPage(1);
    };

    const handleCreate = async () => {
        try {
            await api.post('/comments', newComment);
            setCurrentPage(1);
            setTrigger(t => t + 1);
            setNewComment({
                comment_text: '',
                direction: '',
                time_frame: '',
                percentage_change: '',
                fk_predictor_id: '',
                fk_stock_symbol: '',
            });
            toast.success('Commentaire ajouté avec succès');
        } catch (error) {
            toast.error("Erreur lors de l'ajout du commentaire");
            console.error('Erreur lors de la creation du commentaire :', error);
            throw error;
        }
    };

    const handleDeleteRequest = (id) => {
        setDeleteTarget(id);
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteConfirmed = async () => {
        setIsDeleteDialogOpen(false);
        try {
            await api.delete(`/comments/${deleteTarget}`);
            toast.success('Commentaire supprimé avec succès');
            setCurrentPage(1);
            setTrigger(t => t + 1);
        } catch (error) {
            toast.error('Erreur lors de la suppression');
            console.error('Erreur lors de la suppression du commentaire :', error);
        }
        setDeleteTarget(null);
    };

    const handleUpdate = async () => {
        if (!editComment) return;

        try {
            const response = await api.put(`/comments/${editComment.comment_id}`, editComment);
            setItems((prev) =>
                prev.map((item) =>
                    item.comment_id === editComment.comment_id ? response.data : item
                )
            );
            setEditComment(null);
            toast.success('Commentaire mis à jour avec succès');
        } catch (error) {
            toast.error("Erreur lors de la mise à jour");
            console.error('Erreur lors de la mise a jour du commentaire :', error);
            throw error;
        }
    };

    const handleNextPage = () => {
        const totalPages = Math.ceil(totalItems / rowsPerPage);
        if (currentPage < totalPages) {
            setCurrentPage((prev) => prev + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage((prev) => prev - 1);
        }
    };

    const handlePageClick = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleCompute24h = async () => {
        setComputing(true);
        setComputeMessage('');
        try {
            const token = localStorage.getItem('token');
            const res = await api.post('/kincaid/scores/compute/last24h', {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setComputeMessage(`${res.data.computed} commentaires traites (24h)`);
            setCurrentPage(1);
        } catch (err) {
            setComputeMessage('Erreur: ' + (err.response?.data?.message || err.message));
        } finally {
            setComputing(false);
        }
    };

    const handleComputeByDate = async () => {
        if (!computeDate) return;
        setComputing(true);
        setComputeMessage('');
        try {
            const token = localStorage.getItem('token');
            const res = await api.post('/kincaid/scores/compute/bydate', { date: computeDate }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setComputeMessage(`${res.data.computed} commentaires traites (${computeDate})`);
            setCurrentPage(1);
        } catch (err) {
            setComputeMessage('Erreur: ' + (err.response?.data?.message || err.message));
        } finally {
            setComputing(false);
        }
    };

    const onEdit = (item) => {
        setEditComment(item);
        setEditDialogOpen(true);
    };

    return (
        <Container className="py-6 md:py-10">
            <PageHeader title="Commentaires" />

            {!loading && (
                (() => {
                    const todayStr = new Date().toDateString();
                    const todayComments = items.filter(c => new Date(c.created_at).toDateString() === todayStr).length;
                    const commentMetrics = [
                        { value: totalItems || items.length, label: 'Commentaires totaux' },
                        { value: todayComments, label: "Commentaires aujourd'hui" }
                    ];
                    return <KpiCards metrics={commentMetrics} />;
                })()
            )}

            {/* Create button + dialog */}
            <div className="mb-4">
                <Button onClick={() => setCreateDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter un commentaire
                </Button>
            </div>

            <Suspense fallback={<LazyFallback />}>
                <CreateComment
                    newComment={newComment}
                    setNewComment={setNewComment}
                    onCreate={handleCreate}
                    predictors={predictors}
                    stocks={stocks}
                    isOpen={createDialogOpen}
                    setIsOpen={setCreateDialogOpen}
                />
            </Suspense>

            {/* Kincaid analyze card */}
            <Card className="mb-6">
                <CardHeader>
                    <CardTitle>Outils d'analyse Kincaid</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-wrap gap-3 items-end">
                        <Button onClick={handleCompute24h} disabled={computing} variant="secondary">
                            {computing ? <><Spinner className="h-4 w-4 mr-2" />Calcul...</> : 'Calculer 24h'}
                        </Button>
                        <div className="flex items-end gap-2">
                            <Label htmlFor="compute-date" className="sr-only">Date</Label>
                            <Input
                                id="compute-date"
                                type="date"
                                value={computeDate}
                                onChange={e => setComputeDate(e.target.value)}
                                className="w-40"
                            />
                            <Button onClick={handleComputeByDate} disabled={computing || !computeDate} variant="secondary">
                                {computing ? <><Spinner className="h-4 w-4 mr-2" />Calcul...</> : 'Calculer'}
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Filters */}
            <Card className="mb-6">
                <CardContent className="pt-6">
                    <div className="flex flex-wrap gap-4 items-end">
                        <div className="flex flex-col gap-1.5">
                            <Label htmlFor="filter-re-min" className="text-xs">Reading Ease min</Label>
                            <Input
                                id="filter-re-min"
                                type="number"
                                value={filterInputs.reading_ease_min}
                                onChange={e => setFilterInputs(prev => ({ ...prev, reading_ease_min: e.target.value }))}
                                className="w-28"
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <Label htmlFor="filter-re-max" className="text-xs">Reading Ease max</Label>
                            <Input
                                id="filter-re-max"
                                type="number"
                                value={filterInputs.reading_ease_max}
                                onChange={e => setFilterInputs(prev => ({ ...prev, reading_ease_max: e.target.value }))}
                                className="w-28"
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <Label htmlFor="filter-gl-min" className="text-xs">Grade Level min</Label>
                            <Input
                                id="filter-gl-min"
                                type="number"
                                value={filterInputs.grade_level_min}
                                onChange={e => setFilterInputs(prev => ({ ...prev, grade_level_min: e.target.value }))}
                                className="w-28"
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <Label htmlFor="filter-gl-max" className="text-xs">Grade Level max</Label>
                            <Input
                                id="filter-gl-max"
                                type="number"
                                value={filterInputs.grade_level_max}
                                onChange={e => setFilterInputs(prev => ({ ...prev, grade_level_max: e.target.value }))}
                                className="w-28"
                            />
                        </div>
                        <Button
                            onClick={() => { setActiveFilters({ ...filterInputs }); setTrigger(t => t + 1); setCurrentPage(1); }}
                            className="mb-0"
                            size="sm"
                        >
                            Appliquer les filtres
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Alert for compute result */}
            {computeMessage && (
                <Alert variant={computeMessage.startsWith('Erreur') ? 'destructive' : 'default'} className="mb-4">
                    <AlertTitle>{computeMessage.startsWith('Erreur') ? 'Erreur' : 'Succès'}</AlertTitle>
                    <AlertDescription>{computeMessage}</AlertDescription>
                </Alert>
            )}

            {/* Predictor filter badge */}
            {filterPredictorId && (
                <div className="mb-4">
                    <p className="text-sm text-muted-foreground">
                        Filtre par ID de predicteur : <strong>{filterPredictorId}</strong>
                    </p>
                    <Button variant="outline" size="sm" onClick={resetFilter} className="mt-1">
                        Réinitialiser le filtre
                    </Button>
                </div>
            )}

            {/* Display */}
            {items.length === 0 && !loading ? (
                <EmptyState
                    title="Aucun commentaire trouvé"
                    subtitle="Ajoutez votre premier commentaire pour commencer."
                    actionLabel="Ajouter"
                    onAction={() => setCreateDialogOpen(true)}
                />
            ) : (
                <>
                    <div className="hidden md:block">
                        <CommentTable items={items} loading={loading} onEdit={onEdit} onDeleteRequest={handleDeleteRequest} />
                    </div>
                    <div className="md:hidden">
                        <CommentCards items={items} loading={loading} onEdit={onEdit} onDeleteRequest={handleDeleteRequest} />
                    </div>
                    <Pagination
                        currentPage={currentPage}
                        totalItems={totalItems}
                        rowsPerPage={rowsPerPage}
                        handlePreviousPage={handlePreviousPage}
                        handleNextPage={handleNextPage}
                        handlePageClick={handlePageClick}
                    />
                </>
            )}

            {/* Edit dialog */}
            <EditComment
                editComment={editComment}
                setEditComment={setEditComment}
                onUpdate={handleUpdate}
                predictors={predictors}
                stocks={stocks}
                isOpen={editDialogOpen}
                setIsOpen={setEditDialogOpen}
            />

            {/* Delete confirmation */}
            <DeleteConfirmation
                isOpen={isDeleteDialogOpen}
                onClose={() => setIsDeleteDialogOpen(false)}
                onConfirm={handleDeleteConfirmed}
                title="Confirmer la suppression"
                description="Voulez-vous vraiment supprimer ce commentaire ? Cette action est irréversible."
            />
        </Container>
    );
};

export default Comments;
